import type { JSX, ValidComponent } from "@solidjs/web";
/**
 * `<Dialog.Viewport>` — scrollable positioning container for the dialog
 * popup.
 */

import { dynamic } from "@solidjs/web";
import { createMemo, omit, Show } from "solid-js";
import { composeRefs, userRef } from "../../internals/compose-refs";
import { mergeRootProps } from "../../internals/merge-root-props";
import { resolveClassStyle } from "../../internals/resolve-class-style";
import type { BaseUIComponentProps } from "../../internals/types";
import type { TransitionStatus } from "../../internals/use-transition-status/use-transition-status";
import { useDialogRootContext } from "../dialog-root-context";
import { useDialogPortalContext } from "../portal/dialog-portal-context";

export interface DialogViewportState {
	open: boolean;
	transitionStatus: TransitionStatus;
	nested: boolean;
	nestedDialogOpen: boolean;
}

export interface DialogViewportProps
	extends BaseUIComponentProps<"div", DialogViewportState> {
	/**
	 * Tag/component to render. Defaults to `"div"`. Replaces the
	 * pre-spike `render` prop (axis G).
	 */
	as?: ValidComponent;
}

/**
 * A positioning container for the dialog popup that can be made
 * scrollable. Renders a `<div>` element with `role="presentation"`.
 */
export function DialogViewport(props: DialogViewportProps): JSX.Element {
	const root = useDialogRootContext();
	const keepMounted = useDialogPortalContext();

	const nestedDialogOpen = (): boolean => root.nestedOpenDialogCount() > 0;

	const state = createMemo<DialogViewportState>(() => ({
		open: root.open(),
		transitionStatus: root.transitionStatus(),
		nested: root.nested(),
		nestedDialogOpen: nestedDialogOpen(),
	}));

	const setViewportRef = (el: HTMLDivElement | null): void => {
		root.setViewportElement(el);
	};

	const shouldRender = (): boolean => keepMounted() || root.mounted();

	const internal = {
		role: "presentation",
		get hidden() {
			return !root.mounted();
		},
		get style() {
			return {
				"pointer-events": !root.open() ? "none" : undefined,
			} as JSX.CSSProperties;
		},
		get "data-open"() {
			return state().open ? "" : undefined;
		},
		get "data-closed"() {
			return state().open ? undefined : "";
		},
		get "data-starting-style"() {
			return state().transitionStatus === "starting" ? "" : undefined;
		},
		get "data-ending-style"() {
			return state().transitionStatus === "ending" ? "" : undefined;
		},
		get "data-nested"() {
			return state().nested ? "" : undefined;
		},
		get "data-nested-dialog-open"() {
			return state().nestedDialogOpen ? "" : undefined;
		},
	};

	const userClassStyle = resolveClassStyle(props, () => state());

	const userProps = omit(props, "as", "class", "style", "attrs");

	let merged: Record<string, unknown> = mergeRootProps(
		internal,
		userClassStyle,
	);
	merged = mergeRootProps(merged, userProps);

	const Tag = dynamic(() => props.as ?? "div");
	return (
		<Show when={shouldRender()}>
			<Tag {...merged} ref={composeRefs(setViewportRef, userRef(props))} />
		</Show>
	);
}
